import { Component, inject, signal } from '@angular/core';
import { ActionButton } from '../../../../../shared/action-button/action-button';
import { LoadingIndicator } from '../../../../../shared/loading-indicator/loading-indicator';
import { NotFoundError } from '../../../../../shared/not-found-error/not-found-error';
import { Helpers } from '../../../../../utilities/helpers';
import { ILevel } from '../../../../../interfaces/level';
import { NgClass } from '@angular/common';
import { ClassService } from '../../../../../services/class';
import { ApiEndpoints } from '../../../../../core/api-endpoints';
import { IClasses } from '../../../../../interfaces/class';
import { FlashService } from '../../../../../services/flash';
import { Session } from '../../../../../services/session';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { ITeacher, ITeachers } from '../../../../../interfaces/teacher';
import { TeacherService } from '../../../../../services/teacher';
import { AcademicYearService } from '../../../../../services/academic-year';
import { IAcademicYears } from '../../../../../interfaces/academic-year';
import { ActivatedRoute, Router } from '@angular/router';
import { LevelService } from '../../../../../services/level';
import { Pagination } from '../../../../../shared/pagination/pagination';
import { SubjectService } from '../../../../../services/subject';
import {
  ILevelSubject,
  ILevelSubjects,
  ISubjects,
} from '../../../../../interfaces/subject';
import { TeacherClassSubjectService } from '../../../../../services/teacher-class-subject';
import { ShimmerEffect } from '../../../../../shared/shimmer-effect/shimmer-effect';
import { DashboardService } from '../../../../../services/dashboard';

@Component({
  selector: 'app-level-detail',
  imports: [
    ActionButton,
    LoadingIndicator,
    NotFoundError,
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    Pagination,
    ShimmerEffect,
  ],
  templateUrl: './academic-detail.html',
  styleUrl: './academic-detail.css',
})
export class AcademicDetail {
  constructor(
    private route: ActivatedRoute,
    private levelService: LevelService,
    private router: Router,
    private classService: ClassService,
    private session: Session,
    protected teacherService: TeacherService,
    protected academicYearService: AcademicYearService,
    private subjectService: SubjectService,
    private teacherClassSubjectService: TeacherClassSubjectService,
    private dashboardService: DashboardService,
  ) {
    this.route.queryParamMap.subscribe((params) => {
      this.levelId = params.get('id')!;
    });

    if (!this.levelId) {
      this.router.navigate(['/dashboard/academic-management']);
    }

    this.getLevel(this.levelId);

    const token = this.session.getItem('token');

    this.loggedUserRole = this.session.decodeToken(token)?.role;
    this.schoolId = this.session.decodeToken(token)?.school_id;

    this.getDashboardData();
    this.getClasses();
    this.getLevelSubjects();
    this.getAcademicYears();
    this.getSubjects();
  }

  protected isFiltering = signal<boolean>(false);
  protected classes = signal<IClasses | null>(null);
  protected levelSubjects = signal<ILevelSubjects | null>(null);
  protected readonly Helpers = Helpers;
  protected validStatuses: string[] = ['ACTIVE', 'INACTIVE', 'ARCHIVED'];
  protected changeStatus = signal<boolean>(false);
  protected assignClassTeacher = signal<boolean>(false);
  protected currentFilterType = '';
  protected currentFilterValue = '';
  protected currentSubjectFilterType = '';
  protected currentSubjectFilterValue = '';
  protected selectedLevelSubject!: ILevelSubject['payload'];
  protected pagination = signal({ page: 0, size: 10 });
  protected subjectPagination = signal({ page: 0, size: 10 });
  protected readonly flashService = inject(FlashService);
  protected loggedUserRole?: string;
  protected schoolId?: string;
  protected addClass = signal<boolean>(false);
  protected academicYearId: string = '';
  protected levelId!: string;
  protected nameOfClass: string = '';
  protected classTeacherId: string = '';
  protected capacity!: number;
  protected query!: string;
  protected teachers = signal<ITeachers | null>(null);
  protected teacherName!: string;
  protected teacherPublicId!: string;
  protected academicYears = signal<IAcademicYears | null>(null);
  protected isCreatingClass = signal<boolean>(false);
  protected level = signal<ILevel | null>(null);
  protected assignSubjectTeacherCheck = signal<boolean>(false);
  protected isAssigningSubjectTeacher = signal<boolean>(false);
  protected assignmentRole: string = '';
  protected assignSubjectCheck = signal<boolean>(false);
  protected subjectId: string = '';
  protected subjectList = signal<ISubjects | null>(null);
  protected newStatus: string = '';
  protected currentStatus: string = '';
  protected newSubjectStatus: string = '';
  protected changeSubjectStatusCheck = signal<boolean>(false);
  protected isEditing = signal<boolean>(false);
  protected selectedClassId!: string;
  protected selectedSubjectId!: string;
  protected currentSubjectStatus: string = '';
  protected percentageOfActiveClasses = signal<number>(80);
  protected percentageOfInactiveClasses = signal<number>(30);
  protected percentageOfArchivedClasses = signal<number>(50);
  protected isLoading = signal<boolean>(false);
  protected dashboardData = signal<any>(null);
  protected classList = ['A', 'B', 'C', 'D', 'E'];
  protected filterAcademicYear: string = '';
  protected admins = ['SUPER_ADMIN', 'SCHOOL_ADMIN'];
  protected invalidStatuses = ['INACTIVE', 'COMPLETED', 'ARCHIVED', 'DEACTIVATED'];

  protected existingClasses: {
    name: string;
    level_id: string;
    academic_year_id: string;
  }[] = [];

  protected selectClass(id: string, action: string, value = ''): void {
    if (action === 'status') {
      this.changeStatus.set(true);
      this.currentStatus = value;
    } else if (action === 'subjectStatus') {
      this.changeSubjectStatusCheck.set(true);
      this.currentSubjectStatus = value;
      this.selectedSubjectId = id;
    } else {
      this.assignClassTeacher.set(true);
    }

    this.selectedClassId = id;
  }

  protected getLevel(id: string): void {
    this.isLoading.set(true);

    this.levelService.getLevelById(`${ApiEndpoints.url}/level/${id}`).subscribe({
      next: (value) => {
        this.level.set(value);
        this.isLoading.set(false);
      },
      error: () => {
        this.flashService.success('Could not load Level data');
      },
    });
  }

  protected classExists(className: string): boolean {
    if (!this.levelId || !this.academicYearId) {
      return false;
    }

    return this.existingClasses.some(
      (item) =>
        item.name === className &&
        item.level_id === this.levelId &&
        item.academic_year_id === this.academicYearId,
    );
  }

  protected getClasses(filterType = this.currentFilterType, value = this.currentFilterValue): void {
    this.isFiltering.set(true);

    let { page, size } = this.pagination();

    const params: Record<string, string | number> = {
      page,
      size,
      level_id: this.levelId,
    };

    if (filterType && value) {
      params[filterType] = value;
    }

    this.classService.getClasses(`${ApiEndpoints.url}/class`, params).subscribe({
      next: (res) => {
        this.classes.set(res);
        this.isFiltering.set(false);
        this.existingClasses = res.payload.map((item: any) => ({
          name: item.name,
          level_id: item.level_id,
          academic_year_id: item.academic_year_id,
        }));
      },
      error: (err) => {
        this.isFiltering.set(false);
        this.flashService.failure(
          err?.error?.message ?? 'Could not load classes. Refresh the page',
        );
      },
    });
  }

  protected getLevelSubjects(
    filterType = this.currentSubjectFilterType,
    value = this.currentSubjectFilterValue,
  ): void {
    this.isLoading.set(true);

    this.currentSubjectFilterType = filterType;
    this.currentSubjectFilterValue = value;

    let { page, size } = this.subjectPagination();

    const params: Record<string, string | number> = {
      page,
      size,
      level_id: this.levelId,
    };

    if (filterType && value) {
      params[filterType] = value;
    }

    this.subjectService.getLevelSubjects(`${ApiEndpoints.url}/level-subject`, params).subscribe({
      next: (res) => {
        this.levelSubjects.set(res);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load subjects. Refresh the page',
        );
      },
    });
  }

  protected getSubjects(): void {
    this.subjectService
      .getSubjects(`${ApiEndpoints.url}/subject`, { page: 0, size: 50 })
      .subscribe({
        next: (res) => {
          this.subjectList.set(res);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load subjects. Refresh the page',
          );
        },
      });
  }

  protected setAssignSubjectTeacher(payload: ILevelSubject['payload']): void {
    this.assignSubjectTeacherCheck.set(true);
    this.selectedLevelSubject = payload;
  }

  protected createClass(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    if (!this.schoolId && this.loggedUserRole === 'SUPER_ADMIN') {
      this.flashService.failure('School ID required');
      return;
    }

    if (!this.schoolId) {
      this.flashService.failure('Error creating class. Try again.');
      return;
    }

    this.isCreatingClass.set(true);

    const newClassForm = {
      school_id: this.schoolId,
      academic_year_id: this.academicYearId,
      level_id: this.levelId,
      name: this.nameOfClass,
      class: this.classTeacherId,
      capacity: this.capacity,
      class_teacher_id: this.classTeacherId,
    };

    this.classService.createClass(`${ApiEndpoints.url}/class`, newClassForm).subscribe({
      next: () => {
        this.classTeacherId = '';
        this.addClass.set(false);
        this.flashService.success('Class created successfully.');
        this.getClasses();
        this.getDashboardData();
        this.isCreatingClass.set(false);
        this.teacherName = '';
        this.nameOfClass = '';
        this.academicYearId = '';
        this.capacity = 0;
      },
      error: (err) => {
        this.isCreatingClass.set(false);
        this.flashService.failure(err?.error?.message ?? 'Error creating class. Try again.');
      },
    });
  }

  protected getAcademicYears(): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 30,
    };
    this.academicYearService
      .getAcademicYears(`${ApiEndpoints.url}/academic-year`, params)
      .subscribe({
        next: (res) => {
          this.academicYears.set(res);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load Academic Years. Refresh the page.',
          );
        },
      });
  }

  protected selectTeacher(teacher: ITeacher['payload']) {
    this.query = '';
    this.teacherName = Helpers.fullName({
      first_name: teacher.first_name,
      last_name: teacher.last_name,
    });
    this.teacherPublicId = teacher.public_id;
    this.classTeacherId = teacher.id;
  }

  protected getTeachers(query: string): void {
    this.teacherService.getTeachers(`${ApiEndpoints.url}/teacher`, { search: query }).subscribe({
      next: (res) => {
        this.teachers.set(res);
        this.teacherName = '';
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load teachers. Refresh the page.',
        );
      },
    });
  }

  protected loadPage(page: number): void {
    this.pagination.update((p) => ({
      ...p,
      page,
    }));

    this.getClasses();
  }

  protected loadSubjectPage(page: number): void {
    this.subjectPagination.update((p) => ({
      ...p,
      page,
    }));

    this.getLevelSubjects();
  }

  protected assignSubjectTeacher(form: NgForm): void {
    if (form.invalid && !this.classTeacherId) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    if (!this.schoolId && this.loggedUserRole === 'SUPER_ADMIN') {
      this.flashService.failure('School ID required');
      return;
    }

    if (!this.schoolId) {
      this.flashService.failure('Error assigning Teacher. Try again.');
      return;
    }

    if (!this.selectedLevelSubject) {
      this.flashService.failure('Error assigning Teacher. Try again.');
      return;
    }

    this.isAssigningSubjectTeacher.set(true);

    const teacherSubjectForm = {
      school_id: this.schoolId,
      teacher_id: this.classTeacherId,
      level_subject_id: this.selectedLevelSubject.id,
      assignment_role: this.assignmentRole,
      class_id: this.nameOfClass,
    };

    this.teacherClassSubjectService
      .createTeacherClassSubject(`${ApiEndpoints.url}/teacher-class-subject`, teacherSubjectForm)
      .subscribe({
        next: () => {
          this.isAssigningSubjectTeacher.set(false);
          this.flashService.success('Teacher assigned successfully.');
          this.assignSubjectTeacherCheck.set(false);
          this.getDashboardData();
        },
        error: (err) => {
          this.assignSubjectTeacherCheck.set(false);
          this.flashService.failure(err?.error?.message ?? 'Error assigning Teacher. Try again.');
        },
      });
  }

  protected assignSubject(form: NgForm): void {
    if (form.invalid && !this.classTeacherId) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    if (!this.schoolId && this.loggedUserRole === 'SUPER_ADMIN') {
      this.flashService.failure('School ID required');
      return;
    }

    if (!this.schoolId) {
      this.flashService.failure('Error assigning Subject. Try again.');
      return;
    }

    this.isAssigningSubjectTeacher.set(true);

    const assignSubjectForm = {
      school_id: this.schoolId,
      level_id: this.levelId,
      subject_id: this.subjectId,
    };

    this.subjectService
      .createSubject(`${ApiEndpoints.url}/level-subject`, assignSubjectForm)
      .subscribe({
        next: () => {
          this.subjectId = '';
          this.isAssigningSubjectTeacher.set(false);
          this.assignSubjectCheck.set(false);
          this.getLevelSubjects();
          this.getDashboardData();
          this.flashService.success('Subject assigned successfully.');
        },
        error: (err) => {
          this.isAssigningSubjectTeacher.set(false);
          this.flashService.failure(err?.error?.message ?? 'Error assigning Subject. Try again.');
        },
      });
  }

  protected allSubjectsIncluded(): boolean {
    const allSubjects = this.subjectList()?.payload ?? [];
    const levelSubjects = this.levelSubjects()?.payload ?? [];

    if (!allSubjects.length) {
      return false;
    }

    return allSubjects.every((subject) =>
      levelSubjects.some((levelSubject) => levelSubject.id === subject.id),
    );
  }

  protected changeClassStatus(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    this.isEditing.set(true);

    const editForm = {
      status: this.newStatus,
    };

    this.classService
      .editClass(`${ApiEndpoints.url}/class/${this.selectedClassId}`, editForm)
      .subscribe({
        next: () => {
          this.getClasses();
          this.getDashboardData();
          this.isEditing.set(false);
          this.selectedClassId = '';
          this.newStatus = '';
          this.changeStatus.set(false);
          this.flashService.success('Successfully changed class status');
        },
        error: (err) => {
          this.isEditing.set(false);
          this.flashService.failure(err?.error?.message ?? 'Error changing status. Try again');
        },
      });
  }

  protected assignClassTeacherFn(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    this.isEditing.set(true);

    const editForm = {
      class_teacher_id: this.classTeacherId,
    };

    this.classService
      .editClass(`${ApiEndpoints.url}/class/${this.selectedClassId}`, editForm)
      .subscribe({
        next: () => {
          this.teacherName = '';
          this.teacherPublicId = '';
          this.classTeacherId = '';
          this.getClasses();
          this.getDashboardData();
          this.isEditing.set(false);
          this.selectedClassId = '';
          this.assignClassTeacher.set(false);
          this.flashService.success('Successfully assigned Class Teacher');
        },
        error: (err) => {
          this.isEditing.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Error assigning Class Teacher. Try again',
          );
        },
      });
  }

  protected changeSubjectStatus(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    this.isEditing.set(true);

    const editForm = {
      status: this.newSubjectStatus,
    };

    this.subjectService
      .editSubject(`${ApiEndpoints.url}/level-subject/${this.selectedSubjectId}`, editForm)
      .subscribe({
        next: () => {
          this.getLevelSubjects();
          this.isEditing.set(false);
          this.selectedSubjectId = '';
          this.newSubjectStatus = '';
          this.changeSubjectStatusCheck.set(false);
          this.flashService.success('Successfully changed subject status');
        },
        error: (err) => {
          this.isEditing.set(false);
          this.flashService.failure(err?.error?.message ?? 'Error changing status. Try again');
        },
      });
  }

  protected getDashboardData(): void {
    this.isLoading.set(true);

    this.dashboardService
      .getDashboardData(`${ApiEndpoints.url}/dashboard/level-stats/${this.levelId}`)
      .subscribe({
        next: (res) => {
          this.dashboardData.set(res.payload);
          this.percentageOfActiveClasses.set(
            (this.dashboardData()?.classes?.active / this.dashboardData()?.classes?.total) * 100,
          );
          this.percentageOfActiveClasses.set(
            (this.dashboardData()?.classes?.active / this.dashboardData()?.classes?.total) * 100,
          );
          this.percentageOfInactiveClasses.set(
            (this.dashboardData()?.classes?.inactive / this.dashboardData()?.classes?.total) * 100,
          );
          this.percentageOfArchivedClasses.set(
            (this.dashboardData()?.classes?.archived / this.dashboardData()?.classes?.total) * 100,
          );
          this.isLoading.set(false);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Failed to load dashboard. Refresh the page.',
          );
        },
      });
  }
}
